import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Drawer, Box, IconButton, Typography, Snackbar } from '@mui/material';
import { Close as CloseIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import RichTextEditor from './RichTextEditor';
import { useLiveEditor } from '../../contexts/LiveEditorContext';

const LiveEditButton: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { editedResult, setEditedResult, clearEditor } = useLiveEditor();

  // Process special linebreak characters
  const processedContent = editedResult.replace(/\[\[LINEBREAK\]\]/g, '</br>');

  // Only show the button on specific routes
  const allowedRoutes = ['/social-media-posts', '/ad-generation', '/newsletter'];
  const shouldShow = allowedRoutes.includes(location.pathname);

  // Clear content when path changes
  useEffect(() => {
    clearEditor();
  }, [location.pathname]);

  if (!shouldShow) return null;

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleEditorChange = (content: string) => {
    // Keep the special characters in state, but process them for display
    setEditedResult(content);
  };

  const handleClear = () => {
    clearEditor();
  };

  const copyToClipboard = async () => {
    try {
      // This will capture the HTML content
      const htmlContent = processedContent;

      // Use the Clipboard API with HTML format
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([htmlContent.replace(/<[^>]*>/g, '')], { type: 'text/plain' }),
        }),
      ]);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenDrawer}
        sx={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 1000,
        }}
      >
        {t('common.live_edit')}
      </Button>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: '50%',
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 1,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">{t('common.live_edit')}</Typography>
          <Box>
            <Button
              onClick={copyToClipboard}
              color="primary"
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              sx={{ mr: 1 }}
            >
              {t('common.copy_to_clipboard')}
            </Button>
            <Button
              onClick={handleClear}
              color="error"
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
            >
              {t('common.clear')}
            </Button>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            p: 2,
            maxHeight: 'calc(100vh - 56px)', // Account for the header
          }}
        >
          <RichTextEditor
            label={t('common.edit_content')}
            value={processedContent}
            onChange={handleEditorChange}
            disableContentLengthLimit={true}
            hideLabel={true}
          />
        </Box>
      </Drawer>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        message={t('common.copied_to_clipboard')}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default LiveEditButton;
